//@flow
import { useState, useEffect, useCallback } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import getter from 'dibs-object-getter';
import { Link } from 'dibs-elements/exports/Link';
import { Spinner } from 'dibs-elements/exports/Spinner';

import { logError } from '../../helpers';
import { followProfile } from '../mutations/followProfile';
import { followPage } from '../mutations/followPage';
import { trackDesignFeature } from 'dibs-interiors-photos/exports/trackingHelpers';

import { showLoginModal } from '../../authInterface';

import styles from './ProfileContactInfo.scss';

import type { ProfileFollowButton_viewer as Viewer } from './__generated__/ProfileFollowButton_viewer.graphql';
import type { RelayProp } from 'react-relay';

type Props = {
    viewer: Viewer,
    relay: RelayProp,
    userId?: ?string,
    onLogin: (newUserId: ?string) => void,
};

const getFollowId = (viewer: ?Viewer): ?string => {
    const firmId = getter(viewer).at('tradeFirms').at(0).value('serviceId');

    const userFollows = getter(viewer).at('user').at('favorites').value('edges') || [];

    const anchorSourceIds = userFollows
        .map(userFollow => {
            const sourceId = getter(userFollow).at('node').at('anchor').value('sourceId');

            if (sourceId && firmId && sourceId === firmId) {
                return getter(userFollow).at('node').value('serviceId');
            }
            return null;
        })
        .filter(Boolean);

    return (anchorSourceIds.length && anchorSourceIds[0]) || null;
};

const isUserFollowing = (viewer: ?Viewer): boolean => {
    return !!getFollowId(viewer);
};

function ProfileFollowButtonComponent(props: Props) {
    const [showSpinner, setShowSpinner] = useState(false);
    const [isInLoginFlow, setIsInLoginFlow] = useState(false);
    const isFollowing = isUserFollowing(props.viewer);

    const callUnfollowMutation = () => {
        const favoriteId = getFollowId(props.viewer);
        const page = getter(props.viewer)
            .at('tradeFirms')
            .at(0)
            .at('publicProfile')
            .value('urlLabel');

        if (props.relay && favoriteId && page && props.userId) {
            followPage(props.relay.environment, {
                favoriteId,
                page,
                userId: props.userId,
                type: 'DESIGNER',
                action: 'REMOVE',
            }).then(response => {
                if (response.hasError) {
                    logError(response.error);
                }
                setShowSpinner(false);
            });
        }
    };

    const callFollowMutation = useCallback(() => {
        const userId = props.userId;
        const designerName = getter(props.viewer).at('tradeFirms').at(0).value('name');
        const designerId = getter(props.viewer).at('tradeFirms').at(0).value('serviceId');
        const urlLabel = getter(props.viewer)
            .at('tradeFirms')
            .at(0)
            .at('publicProfile')
            .value('urlLabel');

        // istanbul ignore if
        if (!userId) {
            const error = 'User attempted to follow a profile without a userId present';
            logError(error);
            setShowSpinner(false);
            return;
        }

        if (!props.relay) {
            const error = 'Attempted to follow a profile without a relay environment';
            logError(error);
            setShowSpinner(false);
            return;
        }

        if (!urlLabel) {
            const error = 'Attempted to follow a profile that did not return a URL label';
            logError(error);
            setShowSpinner(false);
            return;
        }

        if (!designerId) {
            const error = 'Attempted to follow a profile that did not return a service ID';
            logError(error);
            setShowSpinner(false);
            return;
        }

        if (!designerName) {
            const error = 'Attempted to follow a profile that did not return a designer name';
            logError(error);
            setShowSpinner(false);
            return;
        }

        followProfile(props.relay.environment, {
            userId,
            page: `/design-firms/${urlLabel}/`,
            designerName,
            designerId,
        }).then(response => {
            if (response.hasError) {
                logError(response.error);
            }
            setShowSpinner(false);
        });
    }, [props.relay, props.userId, props.viewer]);

    const onClickUnfollow = () => {
        if (!showSpinner) {
            setShowSpinner(true);
            callUnfollowMutation();
        }
    };

    const onClickFollow = () => {
        if (!showSpinner) {
            const userId = getter(props).value('userId');
            setShowSpinner(true);
            if (!userId) {
                setIsInLoginFlow(true);
                showLoginModal({
                    onSuccess: res => {
                        props.onLogin(res.userId || '');
                    },
                });
            } else {
                callFollowMutation();
            }
        }
        trackDesignFeature({
            action: 'follow designer',
            label: 'profile',
        });
    };

    useEffect(() => {
        if (props.userId && props.viewer && isInLoginFlow) {
            callFollowMutation();
            setIsInLoginFlow(false);
        }
    }, [callFollowMutation, isInLoginFlow, props.userId, props.viewer]);

    if (!props.viewer) {
        return null;
    }

    return (
        <div className={styles.followButtonWrapper}>
            {isFollowing ? (
                <Link
                    className={styles.followButton}
                    buyerLinkType="standardLinkNoEffect"
                    onClick={onClickUnfollow}
                    dataTn="unfollow-button"
                >
                    <FormattedMessage
                        id="abt.designProfile.header.share.following"
                        defaultMessage="Following"
                    />
                </Link>
            ) : (
                <Link
                    className={styles.followButton}
                    buyerLinkType="standardLinkNoEffect"
                    onClick={onClickFollow}
                    dataTn="follow-button"
                >
                    <FormattedMessage
                        id="abt.designProfile.header.share.follow"
                        defaultMessage="Follow"
                    />
                </Link>
            )}
            {showSpinner ? (
                <div className={styles.spinner} data-tn="follow-firm-spinner-wrapper">
                    <Spinner size={'tiny'} />
                </div>
            ) : null}
        </div>
    );
}

export const ProfileFollowButton = createFragmentContainer(ProfileFollowButtonComponent, {
    viewer: graphql`
        fragment ProfileFollowButton_viewer on Viewer {
            user(userId: $userId) @include(if: $hasUserId) {
                favorites(types: [DESIGNER]) {
                    edges {
                        node {
                            serviceId
                            anchor {
                                sourceId
                            }
                        }
                    }
                }
            }
            tradeFirms(urlLabel: $firmUrlLabel) {
                serviceId
                name
                publicProfile {
                    urlLabel
                }
            }
        }
    `,
});
